'use client';

import classnames from 'classnames';
import { QA_MATCH_CENTRE_LINE_UPS } from '@test-config/testIds';
import { AblyComponent } from '@stadionhq-v3/ably';
import { LineUps } from '@stadionhq-v3/football-ui';
import {
  lineUpsAblyMutator,
  LineUpsLiveProps,
} from '@utils/transforms/components/live/lineUps.transform';
import { useCallback } from 'react';
import styles from './matchCentreLineUps.module.scss';
import { MatchCentreLineUpsProps } from './matchCentreLineUps.types';

const MatchCentreLineUps = ({
  /* props */
  testId = QA_MATCH_CENTRE_LINE_UPS.COMPONENT,
  headshots,
  teamIds,
  home,
  away,
  classNames = [],
  nativeProps = {},
  notification,
  ...ablyProps
}: MatchCentreLineUpsProps) => {
  const classes = classnames([styles.matchCentreLineUps], classNames);

  const renderLineUps = useCallback(
    (liveData: LineUpsLiveProps) => {
      const { teamId, homeTeamId } = teamIds;

      const isClient = homeTeamId === teamId;

      const clientTeam = isClient
        ? { ...home, ...liveData.home }
        : { ...away, ...liveData.away };

      const opposingTeam = isClient
        ? { ...away, ...liveData.away }
        : { ...home, ...liveData.home };

      // Don't show line-ups piecemeal: the two teams' data can arrive on
      // separate updates, so wait until BOTH sides have a starting XI before
      // rendering. Until then show the notification message rather than a blank
      // panel or a bare spinner.
      const bothSidesReady =
        Array.isArray(clientTeam.starting) &&
        clientTeam.starting.length > 0 &&
        Array.isArray(opposingTeam.starting) &&
        opposingTeam.starting.length > 0;

      if (!bothSidesReady) {
        return <>{notification}</>;
      }

      return (
        <>
          <LineUps {...clientTeam} />
          <LineUps {...opposingTeam} />
        </>
      );
    },
    [away, home, teamIds, notification]
  );

  return ablyProps ? (
    <AblyComponent<LineUpsLiveProps, { lineUps: LineUpsLiveProps }>
      {...ablyProps}
      notification={notification}
      storeKey="Line Ups"
      responseKey="lineUps"
      mutator={lineUpsAblyMutator(headshots, teamIds)}
      render={(liveProps: LineUpsLiveProps) => (
        <div className={classes} data-testid={testId} {...nativeProps}>
          {renderLineUps(liveProps)}
        </div>
      )}
    />
  ) : (
    <></>
  );
};

export default MatchCentreLineUps;
